<script>
import BenchmarksMenu from '@modules/consolidator/components/benchmarks-menu/benchmarks-menu.vue'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import { BENCHMARKS } from '@constants/Text'
import { FRACTION_TO_PERCENT } from '@modules/consolidator/constants/AssetMovements'
import { BENCHMARKS_COLORS, coresNomes } from '@constants/Color'
import { formatLines, formatBars_v2 } from '@utils/GraficoData'
import { getNumber, toMoney, toMonthYear } from '@utils/Format'
import {
  buildAssetGoalMarkers,
  buildMarkerTooltipContent,
} from '@modules/consolidator/utils/asset-markers/asset-markers'
import {
  ASSET_CHART_DEFAULT_PERIOD,
  ASSET_CHART_HEIGHT,
  BENCHMARK_PADRAO,
  ASSET_CHART_MODES,
  ASSET_CHART_PERIODS,
  ASSET_CHART_TOP_HEADROOM,
  ASSET_CHART_WIDEST_PERIOD,
  PALETA_BENCHMARK,
} from '@modules/consolidator/constants/AssetMarkers'

const somaDosCodigos = (nome) => {
  let soma = 0
  for (let i = 0; i < nome.length; i += 1) soma += nome.codePointAt(i)
  return soma
}

export default {
  name: 'AssetPerformanceChart',
  components: { BenchmarksMenu, FeedbackWrapper },
  props: {
    /** serie do ativo (dates, rentabilidade_acumulada, pl, benchmark, benchmark_nome) */
    series: {
      type: Object,
      default: null,
    },
    movements: {
      type: Array,
      default: () => [],
    },
    proventos: {
      type: Array,
      default: () => [],
    },
    splits: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** true quando a consulta da serie FALHOU (distinto de "ativo sem serie") */
    erro: {
      type: Boolean,
      default: false,
    },
    /** benchmarks marcados no menu (controlado pelo pai) */
    benchmarksSelecionados: {
      type: Array,
      default: () => [],
    },
    /** key de ASSET_CHART_PERIODS selecionada (controlada pelo pai) */
    period: {
      type: String,
      default: ASSET_CHART_DEFAULT_PERIOD,
    },
    /** moeda do ativo (asset.moeda) — USD exibe disclaimer de conversão */
    moeda: {
      type: String,
      default: '',
    },
    /** título da seção do gráfico */
    title: {
      type: String,
      default: 'Desempenho na carteira',
    },
    /** oculta o botão de alternar Rentabilidade / Patrimônio */
    hideModeToggle: {
      type: Boolean,
      default: false,
    },
    /** oculta o menu de benchmarks */
    hideBenchmarksMenu: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mode: ASSET_CHART_MODES.RENTABILIDADE,
      chartData: {},
      chartOptions: {},
      chartPlugins: [],
      chartKey: 0,
      usuarioInteragiu: false,
      ASSET_CHART_HEIGHT,
    }
  },
  computed: {
    labels() {
      return (this.series && this.series.dates) || []
    },
    rentabilidade() {
      return (this.series && this.series.rentabilidade_acumulada) || []
    },
    plSeries() {
      return (this.series && this.series.pl) || []
    },
    benchmarkNome() {
      return (this.series && this.series.benchmark_nome) || BENCHMARK_PADRAO
    },
    benchmarkMap() {
      return (this.series && this.series.benchmark) || {}
    },
    nomesComSerie() {
      return Object.keys(this.benchmarkMap).filter(
        (nome) => this.benchmarkMap[nome].length
      )
    },
    ordemDeCor() {
      return this.benchmarksDoMenu
        .map((bench) => bench.nome)
        .filter((nome) => !BENCHMARKS_COLORS[nome])
    },
    coresDeBenchmark() {
      const cores = {}
      for (const nome of this.nomesComSerie) {
        const token = BENCHMARKS_COLORS[nome]
        const posicao = this.ordemDeCor.indexOf(nome)
        const indice = posicao >= 0 ? posicao : somaDosCodigos(nome)
        cores[nome] =
          token || PALETA_BENCHMARK[indice % PALETA_BENCHMARK.length]
      }
      return cores
    },
    benchmarksDoEscritorio() {
      const store = this.$store
      return (store && store.getters['settings/benchmarks']) || []
    },
    benchmarksDoMenu() {
      const nomes = this.benchmarksDoEscritorio
        .map((bench) => bench && bench.nome)
        .filter(Boolean)
      const todos = nomes.length ? nomes : BENCHMARKS
      const proprio = this.benchmarkNome
      const lista = todos.includes(proprio) ? todos : [proprio, ...todos]
      return lista.map((nome) => ({ id: nome, nome }))
    },
    nomesDisponiveis() {
      const disponiveis =
        (this.series && this.series.benchmarks_disponiveis) || []
      return new Set(disponiveis)
    },
    isPatrimonio() {
      return this.mode === ASSET_CHART_MODES.PATRIMONIO
    },
    hasRentabilidade() {
      return this.labels.length > 1 && this.rentabilidade.length > 1
    },
    hasPl() {
      return (
        this.labels.length > 1 &&
        this.plSeries.some((value) => value !== null && value !== undefined)
      )
    },
    isUSD() {
      return (this.moeda || '').toUpperCase() === 'USD'
    },
    notaMoeda() {
      if (!this.isUSD) {
        return ''
      }
      return 'Valores convertidos para BRL pela cotação diária do dólar. A rentabilidade inclui variação cambial.'
    },
    naJanelaMaisLarga() {
      return this.period === ASSET_CHART_WIDEST_PERIOD
    },
    temAlgumaSerie() {
      return this.hasRentabilidade || this.hasPl
    },
    mostrarBloco() {
      if (
        this.loading ||
        this.erro ||
        this.temAlgumaSerie ||
        this.usuarioInteragiu
      ) {
        return true
      }
      return !this.naJanelaMaisLarga
    },
    modeOptions() {
      return [
        { value: ASSET_CHART_MODES.RENTABILIDADE, text: 'Rentabilidade' },
        { value: ASSET_CHART_MODES.PATRIMONIO, text: 'Patrimônio' },
      ]
    },
    mensagemSemDados() {
      if (this.erro) {
        return 'Não foi possível carregar o desempenho deste ativo. Troque o período para tentar de novo.'
      }
      if (!this.isPatrimonio && !this.hasRentabilidade && this.hasPl) {
        return 'Não há dados de rentabilidade para o período. Veja em Patrimônio.'
      }
      if (this.isPatrimonio && !this.hasPl && this.hasRentabilidade) {
        return 'Não há dados de patrimônio para o período. Veja em Rentabilidade.'
      }
      return this.isPatrimonio
        ? 'Não há dados de patrimônio para o período.'
        : 'Não há dados de rentabilidade para o período.'
    },
    periodOptions() {
      return ASSET_CHART_PERIODS.map((option) => ({
        value: option.key,
        text: option.label,
      }))
    },
    goals() {
      return buildAssetGoalMarkers({
        movements: this.movements,
        proventos: this.proventos,
        splits: this.splits,
        labels: this.labels,
        ancorarAnteriores: this.naJanelaMaisLarga,
      })
    },
    patrimonioMensal() {
      const porMes = new Map()
      this.labels.forEach((rotulo, indice) => {
        const partes = (rotulo || '').split('/')
        if (partes.length !== 3) {
          return
        }
        const mes = Number(partes[1])
        const ano = Number(partes[2])
        if (!Number.isFinite(mes) || !Number.isFinite(ano)) {
          return
        }
        const bruto = this.plSeries[indice]
        if (bruto === null || bruto === undefined) {
          return
        }
        porMes.set(`${ano}-${mes}`, { mes, ano, valor: getNumber(bruto) || 0 })
      })
      const rotulos = []
      const dados = []
      porMes.forEach(({ mes, ano, valor }) => {
        rotulos.push(toMonthYear(new Date(ano, mes - 1, 1)))
        dados.push(valor)
      })
      return { rotulos, dados }
    },
    hasData() {
      return this.isPatrimonio
        ? this.hasPl && this.patrimonioMensal.dados.length > 0
        : this.hasRentabilidade
    },
    renderInputs() {
      return {
        series: this.series,
        mode: this.mode,
        goals: this.goals,
      }
    },
  },
  watch: {
    renderInputs: {
      handler() {
        this.rebuildChart()
      },
      immediate: true,
    },
  },
  methods: {
    rebuildChart() {
      if (!this.hasData) {
        this.chartData = {}
        this.chartOptions = {}
        this.chartPlugins = []
        return
      }
      const dados = this.buildChartInput()
      const { chartOptions, chartData, chartPlugins } = this.isPatrimonio
        ? formatBars_v2(dados)
        : formatLines(dados, true) // showLegendEndOfLine
      this.chartData = chartData
      this.chartOptions = this.isPatrimonio
        ? chartOptions
        : this.comFolgaNoTopo(chartOptions, dados.data)
      this.chartPlugins = chartPlugins
      this.chartKey += 1
    },
    buildChartInput() {
      const dados = {
        mode: this.isPatrimonio ? 'R$' : '%',
        labels: this.isPatrimonio ? this.patrimonioMensal.rotulos : this.labels,
        data: this.isPatrimonio
          ? this.buildPatrimonioDatasets()
          : this.buildRentabilidadeDatasets(),
      }
      if (this.isPatrimonio) {
        dados.legend = false
        return dados
      }
      dados.goals = this.goals
      dados.applyGoalMarkers = this.goals.length > 0
      dados.goalMarkerOptions = {
        onClick: (goal) => this.handleMarkerClick(goal),
        tooltip: {
          enabled: true,
          getContent: (goal) =>
            buildMarkerTooltipContent(goal, this.formatMarkerItem),
        },
      }
      return dados
    },
    comFolgaNoTopo(chartOptions, datasets) {
      const eixos =
        (chartOptions && chartOptions.scales && chartOptions.scales.yAxes) || []
      if (!eixos.length) {
        return chartOptions
      }
      const valores = (datasets || [])
        .reduce((acc, dataset) => acc.concat(dataset.dados || []), [])
        .map(Number)
        .filter(Number.isFinite)
      if (!valores.length) {
        return chartOptions
      }
      const maior = Math.max.apply(null, valores)
      const menor = Math.min.apply(null, valores)
      const amplitude = maior - menor
      if (amplitude <= 0) {
        return chartOptions
      }
      const folga = amplitude * (ASSET_CHART_TOP_HEADROOM - 1)
      return {
        ...chartOptions,
        scales: {
          ...chartOptions.scales,
          yAxes: eixos.map((eixo, indice) =>
            indice === 0
              ? {
                  ...eixo,
                  ticks: { ...eixo.ticks, suggestedMax: maior + folga },
                }
              : eixo
          ),
        },
      }
    },
    buildRentabilidadeDatasets() {
      const datasets = [
        {
          label: 'Ativo',
          color: coresNomes.verdeInvestimento,
          dados: this.rentabilidade.map(
            (value) => (value || 0) * FRACTION_TO_PERCENT
          ),
          chartOptions: {
            borderWidth: 2,
          },
        },
      ]
      for (const nome of this.nomesComSerie) {
        datasets.push({
          label: nome,
          color: this.coresDeBenchmark[nome],
          dados: this.benchmarkMap[nome].map(
            (value) => (value || 0) * FRACTION_TO_PERCENT
          ),
          chartOptions: {
            borderDash: [5, 5],
            borderWidth: 2,
          },
        })
      }
      return datasets
    },
    buildPatrimonioDatasets() {
      return [
        {
          label: 'Patrimônio',
          color: coresNomes.verdeInvestimento,
          dados: this.patrimonioMensal.dados,
        },
      ]
    },
    formatMarkerItem(item, category) {
      if (category === 'evento') {
        const valor =
          item.valor_total !== null && item.valor_total !== undefined
            ? item.valor_total
            : item.valor
        const nome = item.evento || item.operacao || 'Provento'
        return `${nome} — ${toMoney(getNumber(valor))}`
      }
      if (category === 'split') {
        const nome = item.operacao || 'Split'
        if (item.de && item.para) {
          return `${nome} ${item.de} para ${item.para}`
        }
        return nome
      }
      const nome = item.operacao || 'Movimentação'
      return `${nome} — ${toMoney(getNumber(item.valor))}`
    },
    handleMarkerClick(goal) {
      this.$emit('marker-click', goal.raw || goal)
    },
    changeMode(mode) {
      if (!mode || mode === this.mode) {
        return
      }
      this.usuarioInteragiu = true
      this.mode = mode
    },
    changePeriod(periodKey) {
      if (!periodKey || periodKey === this.period) {
        return
      }
      this.usuarioInteragiu = true
      this.$emit('period-change', periodKey)
    },
  },
}
</script>

<template>
  <div v-if="mostrarBloco" class="asset-performance-chart">
    <div class="d-flex flex-wrap align-center mb-2">
      <h3
        class="az-text-primary az-size-16 font-semibold mr-auto d-flex align-center"
      >
        {{ title }}
        <v-tooltip v-if="notaMoeda" right max-width="320" open-delay="300">
          <template v-slot:activator="{ on, attrs }">
            <icon-svg
              name="mdi-information-outline"
              :size="16"
              color="secondary"
              class="ml-1"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <span>{{ notaMoeda }}</span>
        </v-tooltip>
      </h3>
      <BaseToggleButton
        v-if="!hideModeToggle"
        :value="mode"
        small
        segmented
        class="mr-2"
        :options="modeOptions"
        @change="changeMode"
      />
      <BaseToggleButton
        :value="period"
        small
        segmented
        :options="periodOptions"
        @change="changePeriod"
      />
    </div>
    <div v-if="!isPatrimonio && !hideBenchmarksMenu" class="d-flex justify-end mb-2">
      <BenchmarksMenu
        :value="benchmarksSelecionados"
        :benchmarks="benchmarksDoMenu"
        :available-benchmark-names="nomesDisponiveis"
        @input="$emit('change-benchmarks', $event)"
      />
    </div>

    <FeedbackWrapper
      :loading="loading"
      :no-data="!hasData"
      :message="mensagemSemDados"
      :min-height="ASSET_CHART_HEIGHT"
      bordered
    >
      <bar-chart
        v-if="isPatrimonio"
        :key="chartKey"
        :chart-data="chartData"
        :plugins="chartPlugins"
        :options="chartOptions"
        :height="ASSET_CHART_HEIGHT"
      />
      <line-chart
        v-else
        :key="chartKey"
        :chart-data="chartData"
        :plugins="chartPlugins"
        :options="chartOptions"
        :height="ASSET_CHART_HEIGHT"
      />
    </FeedbackWrapper>
  </div>
</template>
